import { type SARadioStorage } from '@/mocks/superAdminStorage';
import { saRadiosStorage } from '@/mocks/superAdminStorage';

interface RadioStorageTableProps {
  radios: SARadioStorage[];
  filterPlan: string;
  onViewDetail: (radio: SARadioStorage) => void;
  loading: boolean;
  error?: string;
  onRetry: () => void;
}

export default function RadioStorageTable({
  radios,
  filterPlan,
  onViewDetail,
  loading,
  error,
  onRetry,
}: RadioStorageTableProps) {
  const allRadios = saRadiosStorage;

  if (loading) {
    return (
      <div className="bg-background-50 rounded-2xl border border-background-200/70 overflow-hidden">
        <div className="px-5 py-3 border-b border-background-200">
          <div className="h-4 w-32 bg-background-200 rounded animate-pulse"></div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-background-200">
                <th className="text-left px-5 py-2.5"><div className="h-3 w-20 bg-background-200 rounded animate-pulse"></div></th>
                <th className="text-left px-5 py-2.5 w-24"><div className="h-3 w-10 bg-background-200 rounded animate-pulse"></div></th>
                <th className="text-left px-5 py-2.5"><div className="h-3 w-12 bg-background-200 rounded animate-pulse"></div></th>
                <th className="text-right px-5 py-2.5 w-28"><div className="h-3 w-16 bg-background-200 rounded animate-pulse ml-auto"></div></th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 8 }).map((_, i) => (
                <tr key={i} className="border-b border-background-100">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-background-200 animate-pulse shrink-0"></div>
                      <div className="space-y-1">
                        <div className="h-3 w-28 bg-background-200 rounded animate-pulse"></div>
                        <div className="h-2 w-16 bg-background-200 rounded animate-pulse"></div>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3"><div className="h-5 w-12 bg-background-200 rounded animate-pulse"></div></td>
                  <td className="px-5 py-3"><div className="h-1.5 w-full bg-background-200 rounded animate-pulse"></div></td>
                  <td className="px-5 py-3 text-right"><div className="h-7 w-20 bg-background-200 rounded-lg animate-pulse ml-auto"></div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-background-50 rounded-2xl border border-background-200/70 p-12 text-center">
        <div className="w-14 h-14 mx-auto rounded-2xl bg-red-100 flex items-center justify-center mb-4">
          <i className="ri-error-warning-line text-red-500 text-xl"></i>
        </div>
        <p className="text-foreground-800 text-sm font-semibold">Error al cargar datos</p>
        <p className="text-foreground-400 text-[11px] mt-1 mb-4">{error}</p>
        <button
          onClick={onRetry}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-refresh-line text-sm"></i>
          Reintentar
        </button>
      </div>
    );
  }

  if (radios.length === 0) {
    return (
      <div className="bg-background-50 rounded-2xl border border-background-200/70 p-12 text-center">
        <div className="w-14 h-14 mx-auto rounded-2xl bg-background-200 flex items-center justify-center mb-4">
          <i className="ri-radio-line text-foreground-400 text-xl"></i>
        </div>
        <p className="text-foreground-600 text-sm font-semibold">Sin resultados</p>
        <p className="text-foreground-400 text-[11px] mt-1">No hay radios que coincidan con los filtros actuales.</p>
      </div>
    );
  }

  return (
    <div className="bg-background-50 rounded-2xl border border-background-200/70 overflow-hidden">
      <div className="px-5 py-3 border-b border-background-200 flex items-center justify-between">
        <div>
          <h4 className="text-foreground-800 text-xs font-bold">
            Consumo por radio
            {filterPlan !== 'todas' && (
              <span className="ml-2 text-foreground-400 font-normal">— {filterPlan === 'free' ? 'Básico' : filterPlan === 'pro' ? 'Pro' : 'Premium'}</span>
            )}
          </h4>
          <p className="text-foreground-400 text-[10px] mt-0.5">{radios.length} de {allRadios.length} radios</p>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-background-200">
              <th className="text-left px-5 py-2.5 text-[10px] font-semibold text-foreground-400 uppercase tracking-wider">Radio</th>
              <th className="text-left px-5 py-2.5 text-[10px] font-semibold text-foreground-400 uppercase tracking-wider w-24">Plan</th>
              <th className="text-left px-5 py-2.5 text-[10px] font-semibold text-foreground-400 uppercase tracking-wider">Uso</th>
              <th className="text-right px-5 py-2.5 text-[10px] font-semibold text-foreground-400 uppercase tracking-wider w-28"></th>
            </tr>
          </thead>
          <tbody>
            {radios.map((radio) => {
              const pct = Math.round((radio.storageUsedGB / radio.storageTotalGB) * 100);
              const barColor = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-amber-500' : 'bg-emerald-500';
              const planColors: Record<string, string> = {
                free: 'bg-secondary-100 text-secondary-700',
                pro: 'bg-primary-100 text-primary-700',
                premium: 'bg-accent-100 text-accent-700',
              };
              const planLabels: Record<string, string> = { free: 'Básico', pro: 'Pro', premium: 'Premium' };

              return (
                <tr key={radio.id} className="border-b border-background-100 last:border-0 hover:bg-background-100/50 transition-colors">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <img src={radio.logoUrl} alt={radio.name} className="w-8 h-8 rounded-lg object-cover shrink-0" />
                      <div className="min-w-0">
                        <p className="text-xs font-semibold text-foreground-800 truncate max-w-[200px]">{radio.name}</p>
                        <p className="text-foreground-400 text-[10px]">{radio.frequency} · {radio.city}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-md text-[10px] font-semibold ${planColors[radio.plan]}`}>
                      {planLabels[radio.plan]}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <div className="min-w-[140px]">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] font-medium text-foreground-500">
                          {radio.storageUsedGB} / {radio.storageTotalGB} GB
                        </span>
                        <span className={`text-[10px] font-bold ${pct >= 90 ? 'text-red-500' : pct >= 70 ? 'text-amber-500' : 'text-foreground-400'}`}>
                          {pct}%
                        </span>
                      </div>
                      <div className="relative w-full h-1.5 rounded-full bg-background-200 overflow-hidden">
                        <div className={`absolute inset-y-0 left-0 rounded-full ${barColor}`} style={{ width: `${Math.min(pct, 100)}%` }}></div>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button
                      onClick={() => onViewDetail(radio)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-background-100 text-foreground-600 text-[11px] font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-eye-line text-xs"></i>
                      Ver detalle
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}